import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Email, Lock, Person, Business, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme as useMuiTheme } from '@mui/material/styles';

const RegisterContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#07090e',
  backgroundImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(34, 211, 238, 0.08) 0%, transparent 50%)',
  padding: '20px',
}));

const RegisterPaper = styled(Paper)(({ theme }) => ({
  padding: '40px',
  borderRadius: '24px',
  maxWidth: '450px',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '30px',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: theme.palette.background.default,
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.disabled,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    color: theme.palette.text.primary,
  },
}));

const RegisterButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '50px',
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.secondary,
  fontSize: '16px',
  fontWeight: 'bold',
  marginBottom: '20px',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: theme.palette.common.white,
  },
}));

const LoginLink = styled(Box)({
  textAlign: 'center',
  marginTop: '20px',
});

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.companyName || !formData.title || !formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Navigate to Step 2 with form data
    navigate('/register/step2', { state: formData });
  };

  return (
    <RegisterContainer>
      <RegisterPaper elevation={8}>
        <form onSubmit={handleSubmit}>
          <LogoContainer>
            <Box
              component="img"
              src="/SkimBusterHeaderLogin-21.svg"
              alt="SkimBuster by MICCLO"
              sx={{
                height: '140px',
                objectFit: 'contain',
                filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1) brightness(0.9)' : 'none'
              }}
            />
            <Typography
              variant="h5"
              sx={{
                marginTop: 2,
                fontWeight: 'bold',
                color: muiTheme.palette.text.primary,
              }}
            >
              Create Account
            </Typography>
          </LogoContainer>

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2, borderRadius: '16px' }}>
              {error}
            </Alert>
          )}

          <Typography
            variant="body2"
            sx={{
              marginBottom: 3,
              padding: 2,
              backgroundColor: muiTheme.palette.action.hover,
              borderRadius: '16px',
              color: muiTheme.palette.text.secondary,
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            This registration is for admin accounts only. Please ensure you have the proper authorization to create an admin account.
          </Typography>

          <StyledTextField
            fullWidth
            label="Company Name"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Business sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, marginBottom: '20px' }}>
            <StyledTextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
  
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: muiTheme.palette.text.disabled }} />
                  </InputAdornment>
                ),
              }}
            />
            <StyledTextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
  
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: muiTheme.palette.text.disabled }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>



          <StyledTextField
            fullWidth
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}

            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}

            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: muiTheme.palette.text.disabled }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}

            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: muiTheme.palette.text.disabled }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    sx={{ color: muiTheme.palette.text.disabled }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <RegisterButton
            type="submit"
            variant="contained"
          >
            Next Step
          </RegisterButton>

          <LoginLink>
            <Typography color={muiTheme.palette.text.secondary}>
              Already have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={() => navigate('/login')}
                sx={{
                  color: muiTheme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Login
              </Link>
            </Typography>
          </LoginLink>
        </form>
      </RegisterPaper>
    </RegisterContainer>
  );
};

export default Register;